"use client";

import { FC, useEffect, useRef } from "react";
import {
  DotLottieWorkerReact,
  DotLottieWorker,
} from "@lottiefiles/dotlottie-react";

export type LottieBlockProps = {
  src: string;
  autoplay?: boolean;
  loop?: boolean;
  backgroundColor?: string;
  layout?: {
    alignX: number;
    alignY: number;
    fit: "fill" | "contain" | "cover" | "none" | "fit-width" | "fit-height";
  };
};

export const LottieBlock: FC<LottieBlockProps> = ({
  src,
  autoplay = true,
  loop = true,
  backgroundColor,
  layout,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lottieInstanceRef = useRef<DotLottieWorker | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let resizeAnimationFrame: number;

    const observer = new ResizeObserver(() => {
      resizeAnimationFrame = window.requestAnimationFrame(() => {
        if (lottieInstanceRef.current) {
          lottieInstanceRef.current.resize();
        }
      });
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(resizeAnimationFrame);
    };
  }, [src]);

  if (!src) {
    return <div className="bg-red-300">Missing Lottie source!</div>;
  }

  const layoutWithDefaults = {
    alignX: layout?.alignX ?? 0.5,
    alignY: layout?.alignY ?? 0.5,
    fit: layout?.fit ?? "contain",
  };

  return (
    <div
      ref={containerRef}
      className="[&_canvas]:h-full! [&_canvas]:w-full! [&_canvas]:object-cover!"
    >
      <DotLottieWorkerReact
        src={src}
        autoplay={autoplay}
        loop={loop}
        backgroundColor={backgroundColor}
        dotLottieRefCallback={(dotLottie) => {
          lottieInstanceRef.current = dotLottie;
        }}
        layout={{
          align: [layoutWithDefaults.alignX, layoutWithDefaults.alignY],
          fit: layoutWithDefaults.fit,
        }}
      />
    </div>
  );
};
